/**
 * Centralized notification type configuration
 *
 * This file defines all notification types, their icons, colors, and metadata.
 * Icons are sourced from the design system to ensure consistency across the platform.
 *
 * Icon Sources:
 * - 'perspective-icon': Custom CSS class defined in common.scss
 * - 'pi-*': PrimeReact icon library (https://primereact.org/icons)
 */

/**
 * Base notification type configuration stored in the system
 */
export interface NotificationTypeConfigBase {
  value: string;
  labelKey: string; // i18n translation key
  icon: string; // CSS class name for the icon
  color?: string; // Optional: Reserved for future use (e.g., badge colors)
}

/**
 * Notification type configuration with translated label (used at runtime)
 */
export interface NotificationTypeConfig {
  value: string;
  label: string; // Translated label text
  icon: string; // CSS class name for the icon
  color?: string; // Hex color for the notification badge
}

/**
 * All supported notification types (base configuration with translation keys)
 *
 * Icons are displayed as plain icons without colored backgrounds to match
 * the visual style used throughout the platform (PerspectiveMenu, SphereChatPanel, etc.)
 *
 * When adding new notification types:
 * 1. Add the configuration here
 * 2. Add the translation key to localization files (packages/utils/localization)
 * 3. Ensure the icon is available in the design system
 * 4. Use the same icon used elsewhere in the platform for consistency
 * 5. Test in both light and dark themes
 * 6. Update components to handle the new type
 */
export const NOTIFICATION_TYPE_CONFIGS: NotificationTypeConfigBase[] = [
  {
    value: 'sphere-created',
    labelKey: 'notifications.types.perspectiveAssignment',
    icon: 'perspective-icon', // Matches PerspectiveMenu icon
  },
  {
    value: 'conversation-notification',
    labelKey: 'notifications.types.conversation',
    icon: 'pi-comments', // Matches SphereChatPanel "Discussion" icon
  },
  {
    value: 'sphere-status-alert',
    labelKey: 'notifications.types.statusAlert',
    icon: 'pi-exclamation-triangle', // Warning icon for status degradation alerts
  },
];

/**
 * Helper to get notification type configuration by value
 */
export function getNotificationTypeConfig(typeValue: string): NotificationTypeConfigBase | undefined {
  return NOTIFICATION_TYPE_CONFIGS.find((config) => config.value === typeValue);
}

/**
 * Helper to get all notification type values for filtering
 */
export function getNotificationTypeValues(): string[] {
  return NOTIFICATION_TYPE_CONFIGS.map((config) => config.value);
}
